import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MessageSquare, Users, School, ChefHat, Building2, Zap, Clock, CheckCircle, Bell, FileText, Archive, Search, Shield } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PageEcoLoopEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - EcoLoop | Messaging"; }, []);

  return (
    <div className="home-container">
      <HeaderEN currentPage="ecoloop" lang="en" />

      {/* Hero EcoLoop */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <MessageSquare size={16} />
            <span>Integrated Messaging</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span className="gradient-text">EcoLoop</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.2rem', lineHeight: '1.8' }}>
            The structured messaging platform that centralizes all your food service communications
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">4</div>
              <div className="stat-label">Channel types</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Traceability</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">5h</div>
              <div className="stat-label">Saved / week</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section problem-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">The chaos of current communication</h2>
            <p className="section-subtitle">
              Emails, calls, SMS... Information gets lost and nobody has an overview
            </p>
          </div>

          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-icon"><MessageSquare size={32} style={{color: '#84CC16'}} /></div>
              <h3>Scattered emails</h3>
              <p>Conversations lost in multiple email boxes. Impossible to find history.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><Bell size={32} style={{color: '#84CC16'}} /></div>
              <h3>Undocumented calls</h3>
              <p>Decisions made by phone without written trace. Frequent conflicts and misunderstandings.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><Search size={32} style={{color: '#84CC16'}} /></div>
              <h3>Zero traceability</h3>
              <p>Who said what, when, and why? Impossible to know without searching everywhere.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><Clock size={32} style={{color: '#84CC16'}} /></div>
              <h3>Time wasted</h3>
              <p>Hours spent searching for information, asking the same questions, clarifying misunderstandings.</p>
            </div>
          </div>

          <div className="solution-intro">
            <h3>EcoLoop solves all that</h3>
            <p>Integrated messaging that centralizes, structures, and archives all your communications</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">EcoLoop Features</h2>
            <p className="section-subtitle">
              Everything you need to communicate efficiently
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Real-time messaging</h3>
              <p>Instant conversations between all actors. Push notifications for quick responses.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Dedicated channels per actor</h3>
              <p>Parents, Caterers, Schools, Boards. Each relationship has its structured channel.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Ticket system</h3>
              <p>Manage incidents with structured tickets. Status, priority, resolution.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Archive size={32} /></div>
              <h3>Automatic archiving</h3>
              <p>All conversations automatically archived. Complete history accessible with one click.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Search size={32} /></div>
              <h3>Powerful search</h3>
              <p>Find any conversation or decision in seconds with full-text search.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Smart notifications</h3>
              <p>Personalized alerts according to urgency and role. Never miss an important message.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Secure & Private</h3>
              <p>End-to-end encryption. Each actor sees only their relevant conversations.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Read receipts</h3>
              <p>Know when your messages are read. Status tracking (sent, read, replied).</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Attachments</h3>
              <p>Share documents, photos, menus. Everything stays in the conversation context.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section coparent-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">EcoLoop Use Cases</h2>
            <p className="section-subtitle">
              How each actor uses messaging daily
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Parents → Caterers</h3>
              <p><strong>Example:</strong> "My child is allergic to nuts. Can you confirm that Thursday's menu is safe?"<br/>
              Documented and trackable response.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><School size={32} /></div>
              <h3>Schools → Caterers</h3>
              <p><strong>Example:</strong> "We have a special event Friday. Can we adjust the volume to 150 meals?"<br/>
              Simplified coordination.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><ChefHat size={32} /></div>
              <h3>Caterers → Schools</h3>
              <p><strong>Example:</strong> "New menu available next week. Here are the options with nutritional values."<br/>
              Structured information sharing.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Building2 size={32} /></div>
              <h3>Boards → Schools</h3>
              <p><strong>Example:</strong> "New nutritional policy to implement. Here are the directives for all schools."<br/>
              Efficient top-down communication.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Incident management</h3>
              <p><strong>Example:</strong> Missing meal reported → Ticket created → Caterer notified → Resolution documented → Ticket closed.<br/>
              Clear and trackable process.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Feedback & Improvement</h3>
              <p><strong>Example:</strong> School shares student satisfaction scores with caterer to adjust menus.<br/>
              Continuous improvement loop.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="section roi-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why EcoLoop changes everything</h2>
            <p className="section-subtitle">
              Concrete benefits for your organization
            </p>
          </div>

          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-metric">
                <Clock size={40} />
                <h3>Massive time savings</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                No more endless email searches. All information 
                centralized and instantly accessible. <strong>Save 5h/week minimum</strong> 
                per person involved.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <Shield size={40} />
                <h3>Complete traceability</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Every decision documented. Every conversation archived. <strong>Zero litigation risk</strong> 
                thanks to the complete and immutable history.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <Zap size={40} />
                <h3>Maximum responsiveness</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Instant notifications. <strong>Solve problems 3x faster</strong> 
                than with email/phone thanks to structured communication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>
              <div className="footer-column">
                <h4>Technologies</h4>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
                <a onClick={() => navigate('/en/platform')}>Platform</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageEcoLoopEN;
